// --- 1. 경로 타입 생성 (이전과 동일) ---
import { Draft, produce } from 'immer';

type PrevDepth = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, ...0[]];

export type Paths<T, D extends number = 15> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T]-?: K extends string | number
          ?
              | `${K}`
              | (Paths<T[K], PrevDepth[D]> extends infer P ? (P extends string ? `${K}.${P}` : never) : never)
              | (T[K] extends readonly any[] ? `${K}.${number}` : never)
          : never;
      }[keyof T]
    : '';

/**
 * 객체 T에서 경로 P에 해당하는 값의 타입을 추론합니다.
 * 'arrayKey.0' 및 'arrayKey.number'와 같은 경로를 처리합니다.
 * @template T 대상 객체 타입
 * @template P 경로 문자열 (Paths<T> 타입)
 */
export type ValueAtPath<T, P extends string> = P extends `${infer Key}.${infer Rest}` // 경로가 "A.B.C" 또는 "arr.0.prop" 또는 "arr.number.prop" 형태인 경우
  ? Key extends keyof T // Key가 T의 일반적인 속성인 경우 (예: "product", "length")
    ? ValueAtPath<T[Key], Rest>
    : Key extends `${number}` // Key가 문자열 형태의 숫자인 경우 (예: "0", "1")
      ? T extends readonly (infer Item)[] // T가 배열이면
        ? ValueAtPath<Item, Rest> // 배열 아이템 타입으로 재귀
        : undefined // Key는 숫자 형태이지만 T가 배열이 아닌 경우
      : Key extends 'number' // Key가 리터럴 문자열 "number"인 경우 (Paths가 "arr.number" 생성 시)
        ? T extends readonly (infer Item)[] // T가 배열이면
          ? ValueAtPath<Item, Rest> // 배열 아이템 타입으로 재귀
          : undefined // Key가 "number"이지만 T가 배열이 아닌 경우
        : undefined // 위 어떤 경우에도 해당하지 않는 Key
  : P extends keyof T
    ? T[P] // 경로가 단일 세그먼트이고 T의 일반적인 속성인 경우 (예: "name", "length")
    : P extends `${number}` // 경로가 단일 세그먼트이고 문자열 형태의 숫자인 경우 (예: "0")
      ? T extends readonly (infer Item)[] // T가 배열이면
        ? Item // 배열의 아이템 타입 반환
        : undefined // P는 숫자 형태이지만 T가 배열이 아닌 경우
      : P extends 'number' // 경로가 단일 세그먼트이고 리터럴 문자열 "number"인 경우
        ? T extends readonly (infer Item)[] // T가 배열이면
          ? Item // 배열의 아이템 타입 반환
          : undefined // P가 "number"이지만 T가 배열이 아닌 경우
        : undefined; // 위 어떤 경우에도 해당하지 않는 단일 세그먼트 경로

/**
 * 객체에서 지정된 경로의 값을 타입-세이프하게 가져옵니다.
 * @param obj 값을 가져올 대상 객체
 * @param path 가져올 값의 경로 문자열
 * @returns 경로에 해당하는 값, 찾을 수 없으면 undefined
 */
export function getPathValue<TObj, P extends Paths<TObj> & string>(obj: TObj, path: P): ValueAtPath<TObj, P> {
  const segments = path
    .replace(/\[(\d+)\]/g, '.$1') // "arr[0]" -> "arr.0" (런타임에서 유연성 제공)
    .split('.');

  let current: any = obj;

  for (const segment of segments) {
    if (current === null || current === undefined) {
      return undefined as ValueAtPath<TObj, P>;
    }

    const index = parseInt(segment, 10);
    if (Array.isArray(current) && !isNaN(index) && segment === index.toString()) {
      current = current[index];
    } else if (typeof current === 'object' && segment in current) {
      current = current[segment];
    } else {
      return undefined as ValueAtPath<TObj, P>;
    }
  }
  return current as ValueAtPath<TObj, P>;
}

/**
 * Immer를 사용하여 객체의 지정된 경로에 값을 설정하여 새로운 객체를 반환합니다 (불변성 유지).
 *
 * @template T 대상 객체의 타입. Record<string, any>보다 구체적인 것이 좋습니다.
 * @template P 경로 문자열 타입 (Paths<T>를 만족해야 함).
 * @param obj 원본 객체.
 * @param path 값을 설정할 경로 문자열 (예: "user.address.street", "items.0.name").
 * @param value 설정할 값 (ValueAtPath<T, P> 타입을 만족해야 함).
 * @returns 값이 설정된 새로운 객체.
 *
 * @example
 * const obj = { user: { name: "John", address: { city: "New York" } }, items: [{ id: 1, name: "Book" }] };
 * const newObj1 = setValueAtPathWithImmer(obj, "user.address.city", "London");
 * // newObj1.user.address.city === "London"
 * const newObj2 = setValueAtPathWithImmer(obj, "items.0.name", "Pen");
 * // newObj2.items[0].name === "Pen"
 * const newObj3 = setValueAtPathWithImmer(obj, "items.1.id", 2); // 새 아이템 추가 (경로가 존재하지 않으면 생성)
 * // newObj3.items[1].id === 2
 */
export function setValueAtPathWithImmer<T extends object, P extends Paths<T> & string>(
  obj: T,
  path: P,
  value: ValueAtPath<T, P>,
): T {
  if (typeof path !== 'string' || path.trim() === '') {
    return obj;
  }

  return produce(obj, (draft: Draft<T>) => {
    const segments = path.split('.');
    let current: any = draft;

    // 마지막 세그먼트를 제외한 모든 세그먼트에 대해 경로 생성/탐색
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i];
      const nextSegment = segments[i + 1];

      if (segment === undefined || nextSegment === undefined) {
        console.error(
          `[setValueAtPathWithImmer] Unexpected undefined segment during path traversal. Path: "${path}", Index: ${i}. This should not happen with valid paths.`,
        );
        return;
      }

      const currentIndexIsNumber = !isNaN(parseInt(segment, 10)); // segment는 이제 string으로 간주됨
      const nextSegmentIsNumber = !isNaN(parseInt(nextSegment, 10)); // nextSegment는 이제 string으로 간주됨

      let currentSegmentValue = current[segment];

      if (currentIndexIsNumber && Array.isArray(current)) {
        const index = parseInt(segment, 10);
        currentSegmentValue = current[index];
        if (
          currentSegmentValue === undefined ||
          typeof currentSegmentValue !== 'object' ||
          currentSegmentValue === null
        ) {
          current[index] = nextSegmentIsNumber ? [] : {};
        }
        current = current[index];
      } else if (typeof current === 'object' && current !== null) {
        if (
          currentSegmentValue === undefined ||
          typeof currentSegmentValue !== 'object' ||
          currentSegmentValue === null
        ) {
          current[segment] = nextSegmentIsNumber ? [] : {};
        }
        current = current[segment];
      } else {
        console.error(
          `[setValueAtPathWithImmer] Path traversal failed. Segment "${segment}" encountered a non-object/array type. Path: "${path}"`,
        );
        return;
      }
    }

    const lastSegment = segments[segments.length - 1];

    if (lastSegment === undefined) {
      console.error(
        `[setValueAtPathWithImmer] Last segment is undefined. Path: "${path}". This should not happen.`,
      );
      return;
    }

    const lastSegmentIsNumber = !isNaN(parseInt(lastSegment, 10));

    if (lastSegmentIsNumber && Array.isArray(current)) {
      current[parseInt(lastSegment, 10)] = value;
    } else if (typeof current === 'object' && current !== null) {
      current[lastSegment] = value;
    } else {
      console.warn(
        `[setValueAtPathWithImmer] Cannot set value at last segment "${lastSegment}". Target is not an object/array. Path: "${path}"`,
      );
    }
  });
}
